/**
 * JourneyLengthSelector — three durations for the Spinal Healing experience.
 *
 * Presented as a ceremonial choice moment, not a numbered form field:
 *   · Calm the Flare-Up (5 min)
 *   · Daily Healing    (20 min)
 *   · Sleep Recovery   (45 min)
 *
 * The selection is passed up so HealingImmersion can adapt its timings.
 */

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, Sun, Moon, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  'flare-up': Flame,
  'daily': Sun,
  'sleep': Moon,
};

// Bundled fallback if the endpoint is unreachable — keeps the UI serene
// rather than blocking on network.
const FALLBACK = [
  {
    id: 'flare-up',
    label: 'Calm the Flare-Up',
    duration_minutes: 5,
    duration_seconds: 300,
    subtitle: 'When pain is loud and you have a few minutes',
    accent: 'rgba(230,170,150,0.8)',
  },
  {
    id: 'daily',
    label: 'Daily Healing',
    duration_minutes: 20,
    duration_seconds: 1200,
    subtitle: 'The full spinal reset — for your daily rhythm',
    accent: 'rgba(220,190,130,0.85)',
  },
  {
    id: 'sleep',
    label: 'Sleep Recovery',
    duration_minutes: 45,
    duration_seconds: 2700,
    subtitle: 'Drift into deep, restorative rest',
    accent: 'rgba(170,200,240,0.85)',
  },
];

const JourneyLengthSelector = ({ onSelect }) => {
  const [lengths, setLengths] = useState(FALLBACK);
  const [freeMode, setFreeMode] = useState(false);
  const [freeMinutes, setFreeMinutes] = useState(15);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/healing/spinal-ease/journey-lengths`);
        if (r.ok) {
          const d = await r.json();
          if (!cancelled && Array.isArray(d.lengths) && d.lengths.length) {
            setLengths(d.lengths);
          }
        }
      } catch { /* fallback list is already set */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center px-5 py-8"
      style={{
        background: 'radial-gradient(circle at 50% 30%, rgba(30,24,18,1) 0%, rgba(10,8,6,1) 60%)',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.1 }}
      data-testid="journey-length-selector"
    >
      <div className="max-w-md w-full">
        <motion.p
          className="text-white/30 text-[10px] tracking-[0.35em] uppercase text-center mb-2"
          style={{ fontFamily: "'Manrope', sans-serif" }}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1 }}
        >
          Choose your journey
        </motion.p>
        <motion.h2
          className="text-white/85 text-2xl text-center mb-10"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 300 }}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 1.1 }}
        >
          How long shall we hold you today?
        </motion.h2>

        <div className="space-y-3">
          {lengths.map((L, i) => {
            const Icon = ICONS[L.id] || Sun;
            return (
              <motion.button
                key={L.id}
                onClick={() => onSelect(L)}
                whileHover={{ scale: 1.015, y: -1 }}
                whileTap={{ scale: 0.98 }}
                className="w-full text-left rounded-2xl px-5 py-5 flex items-center gap-4 transition-all"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                  border: `1px solid ${L.accent || 'rgba(255,255,255,0.08)'}`,
                  boxShadow: `0 0 40px -12px ${L.accent || 'rgba(255,255,255,0.15)'}`,
                  backdropFilter: 'blur(12px)',
                }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 + i * 0.18, duration: 0.9 }}
                data-testid={`journey-length-${L.id}`}
              >
                <div
                  className="flex items-center justify-center w-11 h-11 rounded-full shrink-0"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${L.accent || 'rgba(255,255,255,0.12)'}`,
                    color: L.accent || 'rgba(255,255,255,0.7)',
                  }}
                >
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p
                      className="text-white/90 text-[15px]"
                      style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic', fontWeight: 400 }}
                    >
                      {L.label}
                    </p>
                    <p
                      className="text-white/50 text-xs shrink-0"
                      style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.06em' }}
                    >
                      {L.duration_minutes} min
                    </p>
                  </div>
                  <p
                    className="text-white/45 text-[12px] mt-1 leading-snug"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {L.subtitle}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>

        {/* Free-play: a custom duration slider (2–60 min) when none of
            the three preset lengths feel right. Collapsed by default so
            the ceremonial choice reads first. */}
        <motion.div
          className="mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 1 }}
        >
          {!freeMode ? (
            <button
              type="button"
              onClick={() => setFreeMode(true)}
              className="w-full text-center text-white/45 text-[12px] py-3 rounded-xl transition-colors hover:text-white/70"
              style={{
                fontFamily: "'Manrope', sans-serif",
                background: 'rgba(255,255,255,0.02)',
                border: '1px dashed rgba(255,255,255,0.10)',
                letterSpacing: '0.05em',
              }}
              data-testid="journey-length-free-toggle"
            >
              <Sparkles size={11} className="inline mr-2 -mt-0.5" />
              Or choose your own length
            </button>
          ) : (
            <motion.div
              className="rounded-2xl p-5"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
                border: '1px solid rgba(220,190,150,0.28)',
                boxShadow: '0 0 40px -14px rgba(220,190,150,0.35)',
                backdropFilter: 'blur(12px)',
              }}
              data-testid="journey-length-free-panel"
            >
              <div className="flex items-baseline justify-between mb-4">
                <p
                  className="text-white/90 text-[14px]"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                >
                  Your own rhythm
                </p>
                <p
                  className="text-white/85 text-[22px] leading-none"
                  style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                  data-testid="journey-length-free-minutes"
                >
                  {freeMinutes}<span className="text-white/50 text-[12px] ml-1.5">min</span>
                </p>
              </div>

              <input
                type="range"
                min={2}
                max={60}
                step={1}
                value={freeMinutes}
                onChange={(e) => setFreeMinutes(parseInt(e.target.value, 10))}
                className="w-full accent-amber-200"
                style={{ accentColor: 'rgba(230,190,130,0.9)' }}
                data-testid="journey-length-free-slider"
              />
              <div className="flex justify-between text-white/30 text-[10px] mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>
                <span>2 min</span>
                <span>30 min</span>
                <span>60 min</span>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setFreeMode(false)}
                  className="flex-1 py-2.5 rounded-xl text-[12px] text-white/55"
                  style={{
                    fontFamily: "'Manrope', sans-serif",
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                  data-testid="journey-length-free-cancel"
                >
                  Back to presets
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onSelect({
                      id: 'custom',
                      label: `${freeMinutes}-Minute Journey`,
                      duration_minutes: freeMinutes,
                      duration_seconds: freeMinutes * 60,
                      subtitle: 'Your own chosen length',
                      accent: 'rgba(220,190,130,0.85)',
                    });
                  }}
                  className="flex-1 py-2.5 rounded-xl text-[12px] text-white/95"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontStyle: 'italic',
                    background: 'linear-gradient(135deg, rgba(230,190,130,0.35) 0%, rgba(180,150,100,0.25) 100%)',
                    border: '1px solid rgba(230,190,130,0.5)',
                    boxShadow: '0 0 30px -12px rgba(230,190,130,0.55)',
                  }}
                  data-testid="journey-length-free-confirm"
                >
                  Begin this journey
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>

        <motion.p
          className="text-center text-white/25 text-[11px] mt-10"
          style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1.2 }}
        >
          There is no wrong choice. Your body knows.
        </motion.p>
      </div>
    </motion.div>
  );
};

export default JourneyLengthSelector;
